/**
 * Google My Business Sync Utilities
 * 
 * This module handles synchronization between GMB Worker API and Supabase
 * for reviews, posts, Q&A, and other GMB data.
 */

import { supabase } from '../lib/supabase'
import {
  discoverAllAccounts,
  getAllReviews,
  getAllPosts,
  getAllQuestions,
  getPerformanceMetrics,
  getSearchKeywords,
  convertStarRating,
  mapLocationToFranchise,
  formatGMBDate,
  parseGMBTimestamp,
  retryGMBCall,
  processBatchWithRateLimit,
  type GMBWorkerResponse
} from './gmbWorkerApi'

// ===== Type Definitions =====

export interface SyncResult {
  success: boolean
  type: 'accounts' | 'locations' | 'reviews' | 'posts' | 'questions' | 'insights'
  itemsSynced: number
  itemsFailed: number
  errors: string[]
  startedAt: Date
  completedAt: Date
  duration: number
}

export interface SyncOptions {
  forceFullSync?: boolean
  locationIds?: string[]
  syncReviews?: boolean
  syncPosts?: boolean
  syncQuestions?: boolean
  syncInsights?: boolean
  batchSize?: number
  delayMs?: number
}

// ===== Account & Location Sync =====

/**
 * Sync all GMB accounts and locations to Supabase
 */
export async function syncAccountsAndLocations(): Promise<SyncResult> {
  const startedAt = new Date()
  const errors: string[] = []
  let itemsSynced = 0
  let itemsFailed = 0

  try {
    // Discover all accounts and locations
    const discoveryResult = await retryGMBCall(() => discoverAllAccounts())
    
    if (!discoveryResult.success || !discoveryResult.data) {
      throw new Error(discoveryResult.error || 'Failed to discover accounts')
    }

    const { accounts, locations } = discoveryResult.data

    // Sync accounts
    for (const account of accounts) {
      try {
        const { error } = await supabase
          .from('gmb_accounts')
          .upsert({
            gmb_account_id: account.id,
            account_name: account.name,
            account_type: account.type,
            role: account.role,
            state: account.state,
            discovered_at: account.discovered_at,
            last_synced_at: new Date().toISOString()
          }, {
            onConflict: 'gmb_account_id'
          })

        if (error) throw error
        itemsSynced++
      } catch (error) {
        itemsFailed++
        errors.push(`Account ${account.id}: ${error.message}`)
      }
    }

    // Sync locations
    for (const location of locations) {
      try {
        // Map to franchise region
        const franchiseRegion = mapLocationToFranchise(location.name, location.address)

        const { error } = await supabase
          .from('gmb_locations')
          .upsert({
            gmb_api_location_id: location.id, // This is the real GMB API location ID
            gmb_account_id: location.account_id,
            location_name: location.name,
            place_id: location.place_id,
            primary_phone: location.phone,
            website_url: location.website,
            address_street: location.address,
            latitude: location.lat_lng?.latitude,
            longitude: location.lat_lng?.longitude,
            franchise_region: franchiseRegion,
            discovered_at: new Date().toISOString(),
            last_synced_at: new Date().toISOString()
          }, {
            onConflict: 'gmb_api_location_id'
          })

        if (error) throw error
        itemsSynced++
      } catch (error) {
        itemsFailed++
        errors.push(`Location ${location.id}: ${error.message}`)
      }
    }

    // Log sync operation
    await logSyncOperation('accounts', itemsSynced, itemsFailed, errors, startedAt)

    return {
      success: itemsFailed === 0,
      type: 'accounts',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    errors.push(errorMessage)
    
    await logSyncOperation('accounts', itemsSynced, itemsFailed, errors, startedAt)
    
    return {
      success: false,
      type: 'accounts',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  }
}

// ===== Reviews Sync =====

/**
 * Sync reviews for specified locations or all locations
 */
export async function syncReviews(locationIds?: string[]): Promise<SyncResult> {
  const startedAt = new Date()
  const errors: string[] = []
  let itemsSynced = 0
  let itemsFailed = 0

  try {
    // Get locations to sync
    const locationsToSync = await getLocationsToSync(locationIds)

    // Process each location
    for (const location of locationsToSync) {
      try {
        // Check if we have a real GMB API location ID
        const gmbLocationId = location.gmb_api_location_id
        if (!gmbLocationId) {
          console.warn(`Skipping location ${location.location_name}: No GMB API location ID found. Complete OAuth setup first.`)
          itemsFailed++
          errors.push(`Location ${location.location_name}: Missing GMB API location ID`)
          continue
        }

        const reviews = await getAllReviews(gmbLocationId)

        // Map to the content locations table by name matching
        // The locations table is for content management, not GMB
        const { data: locationData, error: locationError } = await supabase
          .from('locations')
          .select('id, name')
          .ilike('name', `%${location.location_name}%`)
          .maybeSingle()
        
        if (locationError) {
          console.warn(`Failed to find matching location for ${location.location_name}:`, locationError)
        }
        
        // Get existing reviews for this location with key fields to check completeness
        // NOTE: Legacy reviews have GMB IDs in 'review_id' field but 'gmb_review_id' is null
        const { data: existingReviews } = await supabase
          .from('reviews')
          .select(`
            id,
            review_id,
            gmb_review_id, 
            reviewer_update_time, 
            reply_update_time,
            reviewer_photo_url,
            location_id,
            auto_tagged,
            reviewer_name,
            star_rating,
            source
          `)
          .or(`gmb_location_id.eq.${gmbLocationId},and(location_name.ilike.%${location.location_name}%,source.eq.manual)`)

        // Create map for both legacy and new GMB reviews
        const existingReviewMap = new Map()
        existingReviews?.forEach(r => {
          // Map by gmb_review_id if it exists (new format)
          if (r.gmb_review_id) {
            existingReviewMap.set(r.gmb_review_id, r)
          }
          // Also map by review_id for legacy reviews (old format)
          if (r.review_id && r.source === 'manual') {
            existingReviewMap.set(r.review_id, r)
          }
        })

        // Function to check if existing review needs data enhancement
        const needsDataUpdate = (review: any, existing: any): boolean => {
          // Check for missing critical fields
          const missingPhotoUrl = !existing.reviewer_photo_url && review.reviewer?.profilePhotoUrl
          const missingLocationId = !existing.location_id && locationData?.id
          const missingAutoTag = existing.auto_tagged === null
          const missingReviewerName = !existing.reviewer_name && review.reviewer?.displayName
          const missingStarRating = !existing.star_rating && review.starRating
          
          // Check if this is a legacy review that needs migration
          const isLegacyReview = existing.source === 'manual' && !existing.gmb_review_id && existing.review_id
          
          return missingPhotoUrl || missingLocationId || missingAutoTag || missingReviewerName || missingStarRating || isLegacyReview
        }

        // Filter reviews to include new, updated, or incomplete ones
        const reviewsToSync = reviews.filter(review => {
          const existing = existingReviewMap.get(review.name)
          
          if (!existing) {
            return true // New review
          }
          
          // Check if review or reply has been updated
          const reviewUpdated = review.updateTime !== existing.reviewer_update_time
          const replyUpdated = review.reviewReply?.updateTime !== existing.reply_update_time
          
          // Check if existing review is missing important data
          const needsEnhancement = needsDataUpdate(review, existing)
          
          return reviewUpdated || replyUpdated || needsEnhancement
        })

        // Only sync if there are new/updated reviews
        if (reviewsToSync.length > 0) {
          // Count different types of updates
          const newReviews = reviewsToSync.filter(r => !existingReviewMap.has(r.name))
          const updatedReviews = reviewsToSync.filter(r => {
            const existing = existingReviewMap.get(r.name)
            return existing && (
              r.updateTime !== existing.reviewer_update_time || 
              r.reviewReply?.updateTime !== existing.reply_update_time
            )
          })
          const enhancedReviews = reviewsToSync.filter(r => {
            const existing = existingReviewMap.get(r.name)
            return existing && needsDataUpdate(r, existing) && 
              r.updateTime === existing.reviewer_update_time &&
              r.reviewReply?.updateTime === existing.reply_update_time
          })
          
          const legacyMigrations = reviewsToSync.filter(r => {
            const existing = existingReviewMap.get(r.name)
            return existing && existing.source === 'manual' && !existing.gmb_review_id
          })
          
          console.log(`Syncing ${reviewsToSync.length} reviews for ${location.location_name}: ${newReviews.length} new, ${updatedReviews.length} updated, ${enhancedReviews.length} enhanced, ${legacyMigrations.length} legacy migrated`)
          
          // Batch insert/update reviews to the main reviews table
          const reviewBatch = reviewsToSync.map(review => {
            const existing = existingReviewMap.get(review.name)
            const isLegacyUpdate = existing && existing.source === 'manual' && !existing.gmb_review_id
            
            return {
              // Use existing ID for legacy reviews, otherwise let DB generate
              ...(isLegacyUpdate ? { id: existing.id } : {}),
              
              // GMB specific fields
              gmb_review_id: review.name,
              gmb_location_id: gmbLocationId,
              source: 'gmb' as const,
              
              // Map to existing review fields
              reviewer_name: review.reviewer.displayName,
              reviewer_photo_url: review.reviewer.profilePhotoUrl,
              star_rating: convertStarRating(review.starRating), // Converts to "ONE", "TWO", etc.
              reviewer_comment: review.comment || '',
              reply_comment: review.reviewReply?.comment,
              reply_update_time: review.reviewReply?.updateTime,
              reviewer_create_time: review.createTime,
              reviewer_update_time: review.updateTime,
              
              // Location mapping
              location_id: locationData?.id || null,
              location_name: location.location_name,
              
              // Default values for required fields
              auto_tagged: false,
              tag_confidence: null,
              
              // Timestamps
              create_time: review.createTime,
              update_time: review.updateTime,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          })

          // Use upsert to handle duplicates based on gmb_review_id
          const { error } = await supabase
            .from('reviews')
            .upsert(reviewBatch, {
              onConflict: 'gmb_review_id',
              ignoreDuplicates: false
            })

          if (error) throw error
          itemsSynced += reviewBatch.length
        } else {
          console.log(`No new reviews to sync for ${location.location_name}`)
        }

        // Update location metrics
        await updateLocationMetrics(gmbLocationId)

      } catch (error) {
        itemsFailed++
        errors.push(`Location ${gmbLocationId || location.location_name}: ${error.message}`)
      }
    }

    // Log sync operation
    await logSyncOperation('reviews', itemsSynced, itemsFailed, errors, startedAt)

    return {
      success: itemsFailed === 0,
      type: 'reviews',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    errors.push(errorMessage)
    
    await logSyncOperation('reviews', itemsSynced, itemsFailed, errors, startedAt)
    
    return {
      success: false,
      type: 'reviews',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  }
}

// ===== Posts Sync =====

/**
 * Sync posts for specified locations or all locations
 */
export async function syncPosts(locationIds?: string[]): Promise<SyncResult> {
  const startedAt = new Date()
  const errors: string[] = []
  let itemsSynced = 0
  let itemsFailed = 0

  try {
    // Get locations to sync
    const locationsToSync = await getLocationsToSync(locationIds)

    // Process each location
    for (const location of locationsToSync) {
      try {
        const posts = await getAllPosts(location.gmb_location_id)

        // Batch insert/update posts
        const postBatch = posts.map(post => ({
          gmb_post_id: post.name,
          gmb_location_id: location.gmb_location_id,
          language_code: post.languageCode,
          summary: post.summary,
          topic_type: post.topicType,
          state: post.state,
          cta_type: post.callToAction?.actionType,
          cta_url: post.callToAction?.url,
          media_urls: post.media?.map(m => m.sourceUrl) || [],
          
          // Event details
          event_title: post.event?.title,
          event_start_date: post.event?.schedule?.startDate ? 
            formatEventDate(post.event.schedule.startDate) : null,
          event_end_date: post.event?.schedule?.endDate ? 
            formatEventDate(post.event.schedule.endDate) : null,
          
          // Offer details
          offer_coupon_code: post.offer?.couponCode,
          offer_redeem_url: post.offer?.redeemOnlineUrl,
          offer_terms: post.offer?.termsConditions,
          
          // Metrics
          view_count: post.insights?.viewCount || 0,
          click_count: post.insights?.searchCount || 0,
          
          // Timestamps
          gmb_created_at: post.createTime,
          gmb_updated_at: post.updateTime,
          synced_at: new Date().toISOString()
        }))

        if (postBatch.length > 0) {
          const { error } = await supabase
            .from('gmb_posts')
            .upsert(postBatch, {
              onConflict: 'gmb_post_id'
            })

          if (error) throw error
          itemsSynced += postBatch.length
        }

      } catch (error) {
        itemsFailed++
        errors.push(`Location ${location.gmb_location_id}: ${error.message}`)
      }
    }

    // Log sync operation
    await logSyncOperation('posts', itemsSynced, itemsFailed, errors, startedAt)

    return {
      success: itemsFailed === 0,
      type: 'posts',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    errors.push(errorMessage)
    
    await logSyncOperation('posts', itemsSynced, itemsFailed, errors, startedAt)
    
    return {
      success: false,
      type: 'posts',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  }
}

// ===== Questions Sync =====

/**
 * Sync Q&A for specified locations or all locations
 */
export async function syncQuestions(locationIds?: string[]): Promise<SyncResult> {
  const startedAt = new Date()
  const errors: string[] = []
  let itemsSynced = 0
  let itemsFailed = 0

  try {
    // Get locations to sync
    const locationsToSync = await getLocationsToSync(locationIds)

    // Process each location
    for (const location of locationsToSync) {
      try {
        const questions = await getAllQuestions(location.gmb_location_id)

        // Batch insert/update questions
        const questionBatch = questions.map(question => ({
          gmb_question_id: question.name,
          gmb_location_id: location.gmb_location_id,
          author_name: question.author.displayName,
          text: question.text,
          
          // Get the first merchant answer if any
          answer_text: question.answers?.find(a => a.author.type === 'MERCHANT')?.text,
          is_answered: question.answers?.some(a => a.author.type === 'MERCHANT') || false,
          
          upvote_count: question.upvoteCount,
          gmb_created_at: question.createTime,
          gmb_updated_at: question.updateTime,
          synced_at: new Date().toISOString()
        }))

        if (questionBatch.length > 0) {
          const { error } = await supabase
            .from('gmb_questions')
            .upsert(questionBatch, {
              onConflict: 'gmb_question_id'
            })

          if (error) throw error
          itemsSynced += questionBatch.length
        }

        // Update location metrics
        await updateLocationMetrics(location.gmb_location_id)

      } catch (error) {
        itemsFailed++
        errors.push(`Location ${location.gmb_location_id}: ${error.message}`)
      }
    }

    // Log sync operation
    await logSyncOperation('questions', itemsSynced, itemsFailed, errors, startedAt)

    return {
      success: itemsFailed === 0,
      type: 'questions',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    errors.push(errorMessage)
    
    await logSyncOperation('questions', itemsSynced, itemsFailed, errors, startedAt)
    
    return {
      success: false,
      type: 'questions',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  }
}

// ===== Insights Sync =====

/**
 * Sync performance metrics and insights for locations
 */
export async function syncInsights(locationIds?: string[], daysBack: number = 30): Promise<SyncResult> {
  const startedAt = new Date()
  const errors: string[] = []
  let itemsSynced = 0
  let itemsFailed = 0

  try {
    // Get locations to sync
    const locationsToSync = await getLocationsToSync(locationIds)

    // Calculate date range
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - daysBack)

    // Process each location
    for (const location of locationsToSync) {
      try {
        // Get performance metrics
        const metricsResult = await retryGMBCall(() => 
          getPerformanceMetrics(
            location.gmb_location_id,
            formatGMBDate(startDate),
            formatGMBDate(endDate)
          )
        )

        if (metricsResult.success && metricsResult.data?.metrics) {
          // Process metrics by date
          const metricsByDate = new Map<string, any>()

          for (const metric of metricsResult.data.metrics) {
            for (const value of metric.values) {
              const date = value.date
              if (!metricsByDate.has(date)) {
                metricsByDate.set(date, {
                  gmb_location_id: location.gmb_location_id,
                  date,
                  views_search: 0,
                  views_maps: 0,
                  actions_website: 0,
                  actions_phone: 0,
                  actions_directions: 0
                })
              }

              const dayMetrics = metricsByDate.get(date)
              
              // Map metric types to database columns
              switch (metric.metric) {
                case 'QUERIES_DIRECT':
                case 'QUERIES_INDIRECT':
                  dayMetrics.views_search += value.value
                  break
                case 'VIEWS_MAPS':
                  dayMetrics.views_maps = value.value
                  break
                case 'ACTIONS_WEBSITE':
                  dayMetrics.actions_website = value.value
                  break
                case 'ACTIONS_PHONE':
                  dayMetrics.actions_phone = value.value
                  break
                case 'ACTIONS_DRIVING_DIRECTIONS':
                  dayMetrics.actions_directions = value.value
                  break
              }
            }
          }

          // Batch insert insights
          const insightsBatch = Array.from(metricsByDate.values())
          
          if (insightsBatch.length > 0) {
            const { error } = await supabase
              .from('gmb_insights')
              .upsert(insightsBatch, {
                onConflict: 'gmb_location_id,date'
              })

            if (error) throw error
            itemsSynced += insightsBatch.length
          }
        }

        // Get search keywords
        const keywordsResult = await retryGMBCall(() => 
          getSearchKeywords(location.gmb_location_id)
        )

        if (keywordsResult.success && keywordsResult.data?.keywords) {
          // Store keywords as JSON in location record
          const { error } = await supabase
            .from('gmb_locations')
            .update({
              search_keywords: keywordsResult.data.keywords,
              keywords_updated_at: new Date().toISOString()
            })
            .eq('gmb_location_id', location.gmb_location_id)

          if (error) throw error
        }

      } catch (error) {
        itemsFailed++
        errors.push(`Location ${location.gmb_location_id}: ${error.message}`)
      }
    }

    // Log sync operation
    await logSyncOperation('insights', itemsSynced, itemsFailed, errors, startedAt)

    return {
      success: itemsFailed === 0,
      type: 'insights',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    errors.push(errorMessage)
    
    await logSyncOperation('insights', itemsSynced, itemsFailed, errors, startedAt)
    
    return {
      success: false,
      type: 'insights',
      itemsSynced,
      itemsFailed,
      errors,
      startedAt,
      completedAt: new Date(),
      duration: Date.now() - startedAt.getTime()
    }
  }
}

// ===== Full Sync =====

/**
 * Perform a full sync of all GMB data
 */
export async function performFullSync(options: SyncOptions = {}): Promise<{
  accounts: SyncResult
  reviews?: SyncResult
  posts?: SyncResult
  questions?: SyncResult
  insights?: SyncResult
}> {
  const results: any = {}

  // Always sync accounts and locations first
  console.log('Syncing GMB accounts and locations...')
  results.accounts = await syncAccountsAndLocations()

  // Only proceed if accounts sync was successful
  if (results.accounts.success) {
    // Sync reviews
    if (options.syncReviews !== false) {
      console.log('Syncing GMB reviews...')
      results.reviews = await syncReviews(options.locationIds)
    }

    // Sync posts
    if (options.syncPosts !== false) {
      console.log('Syncing GMB posts...')
      results.posts = await syncPosts(options.locationIds)
    }

    // Sync questions
    if (options.syncQuestions !== false) {
      console.log('Syncing GMB questions...')
      results.questions = await syncQuestions(options.locationIds)
    }

    // Sync insights
    if (options.syncInsights !== false) {
      console.log('Syncing GMB insights...')
      results.insights = await syncInsights(options.locationIds)
    }
  }

  return results
}

// ===== Helper Functions =====

/**
 * Get locations to sync based on provided IDs or all active locations
 */
async function getLocationsToSync(locationIds?: string[]) {
  if (locationIds && locationIds.length > 0) {
    // Use gmb_api_location_id for filtering if locationIds contain GMB API format
    // Otherwise use the generated gmb_location_id (UUID format)
    const isGmbApiFormat = locationIds.some(id => id.includes('accounts/'))
    const filterColumn = isGmbApiFormat ? 'gmb_api_location_id' : 'gmb_location_id'
    
    const { data, error } = await supabase
      .from('gmb_locations')
      .select('*')
      .in(filterColumn, locationIds)
      .eq('is_active', true)

    if (error) throw error
    return data || []
  } else {
    const { data, error } = await supabase
      .from('gmb_locations')
      .select('*')
      .eq('is_active', true)

    if (error) throw error
    return data || []
  }
}

/**
 * Update location metrics (reviews count, rating, etc)
 */
async function updateLocationMetrics(locationId: string) {
  try {
    // Get review stats from main reviews table
    // Handle both gmb_api_location_id and gmb_location_id formats
    const { data: reviewStats } = await supabase
      .from('reviews')
      .select('star_rating')
      .eq('gmb_location_id', locationId)
      .eq('source', 'gmb')

    const totalReviews = reviewStats?.length || 0
    let averageRating = 0
    
    if (totalReviews > 0) {
      // Convert star ratings to numbers
      const numericRatings = reviewStats.map(r => {
        switch (r.star_rating) {
          case 'FIVE': return 5
          case 'FOUR': return 4
          case 'THREE': return 3
          case 'TWO': return 2
          case 'ONE': return 1
          default: return 0
        }
      })
      averageRating = numericRatings.reduce((sum: number, r: number) => sum + r, 0) / totalReviews
    }

    // Get unanswered questions count
    const { count: unansweredQuestions } = await supabase
      .from('gmb_questions')
      .select('*', { count: 'exact', head: true })
      .eq('gmb_location_id', locationId)
      .eq('is_answered', false)

    // Update location - try both gmb_api_location_id and gmb_location_id
    const isGmbApiFormat = locationId.includes('accounts/')
    const updateColumn = isGmbApiFormat ? 'gmb_api_location_id' : 'gmb_location_id'
    
    await supabase
      .from('gmb_locations')
      .update({
        total_reviews: totalReviews,
        average_rating: Math.round(averageRating * 10) / 10,
        unanswered_questions: unansweredQuestions || 0,
        last_synced_at: new Date().toISOString()
      })
      .eq(updateColumn, locationId)

  } catch (error) {
    console.error(`Failed to update metrics for location ${locationId}:`, error)
  }
}

/**
 * Log sync operation to database
 */
async function logSyncOperation(
  syncType: string,
  itemsSynced: number,
  itemsFailed: number,
  errors: string[],
  startedAt: Date
) {
  try {
    await supabase
      .from('gmb_sync_log')
      .insert({
        sync_type: syncType,
        status: itemsFailed === 0 ? 'success' : itemsFailed < itemsSynced ? 'partial' : 'failed',
        items_synced: itemsSynced,
        items_failed: itemsFailed,
        error_message: errors.length > 0 ? errors.join('; ') : null,
        started_at: startedAt.toISOString(),
        completed_at: new Date().toISOString()
      })
  } catch (error) {
    console.error('Failed to log sync operation:', error)
  }
}

/**
 * Format event date from GMB format
 */
function formatEventDate(date: { year: number; month: number; day: number }): string {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

// ===== Export Additional Utilities =====

export {
  getLocationsToSync,
  updateLocationMetrics,
  logSyncOperation
}